import { useQuery } from '@tanstack/react-query';
import { useMemo } from 'react';

import type { UploadFn } from '@/lib/upload';
import { createPresignedUploader } from '@/lib/upload';

import {
  devicesResource,
  getDevicePresignedUrl,
  listMetaCategories,
  listMetaComponents,
} from './api';

/** Two presigned uploaders (images + installation-guide), memoised per mount. The server
 *  derives the storage path from the `attachmentType` passed to the presign request. */
export function useDeviceUploaders(): { imageUploader: UploadFn; guideUploader: UploadFn } {
  return useMemo(
    () => ({
      imageUploader: createPresignedUploader({
        getPresignedUrl: (file, ctx) => getDevicePresignedUrl(file, 'image', ctx),
      }),
      guideUploader: createPresignedUploader({
        getPresignedUrl: (file, ctx) => getDevicePresignedUrl(file, 'manual', ctx),
      }),
    }),
    [],
  );
}

export const deviceKeys = {
  ...devicesResource.keys,
  metaCategories: () => [...devicesResource.keys.all, 'meta', 'categories'] as const,
  metaComponents: () => [...devicesResource.keys.all, 'meta', 'components'] as const,
};

/** Active categories for the filter dropdown; cached 5 min (rarely changes). */
export function useMetaCategories() {
  return useQuery({
    queryKey: deviceKeys.metaCategories(),
    queryFn: listMetaCategories,
    staleTime: 5 * 60 * 1000,
  });
}

/** Selectable components for the Applicable Components filter; cached 5 min. */
export function useMetaComponents() {
  return useQuery({
    queryKey: deviceKeys.metaComponents(),
    queryFn: listMetaComponents,
    staleTime: 5 * 60 * 1000,
  });
}

/** Device CRUD query hooks. Created once by the shared catalog-resource factory; the
 *  listing query key is prefixed `['devices']`, so every write invalidates and refreshes it. */
export const useDevice = devicesResource.useDetail;
export const useCreateDevice = devicesResource.useCreate;
export const useUpdateDevice = devicesResource.useUpdate;
export const useSetDeviceStatus = devicesResource.useSetStatus;
export const useDeleteDevice = devicesResource.useDelete;
